import React from "react";
import { Trans, useTranslation } from "react-i18next";

export type ReviewCockpitPriority = "stat" | "urgent" | "routine";
export type SlaClockState =
  | "overdue"
  | "approaching"
  | "on_track"
  | "not_applicable";

export type ReviewCockpitRow = {
  studyId: string;
  reportId?: string;
  patientLabel: string;
  priority: ReviewCockpitPriority;
  qeegThumbnailUrl: string | null;
  aiCandidateCount: number;
  reportStatus: string;
  assignedTo: string | null;
  slaRemainingSeconds: number | null;
  slaState: SlaClockState;
  keyboardNavIndex: number;
};

export function buildSparklineThumbnail(values: number[]): string {
  const width = 72;
  const height = 28;
  const series = values.length > 1 ? values : [0, ...values, 0];
  const max = Math.max(...series.map((value) => Math.abs(value)), 1);
  const points = series
    .map((value, index) => {
      const x = (index / Math.max(series.length - 1, 1)) * width;
      const y = height - ((value + max) / (2 * max)) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" aria-hidden="true"><polyline fill="none" stroke="#0d6efd" stroke-width="1.5" points="${points}"/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function formatSlaClock(
  remainingSeconds: number | null,
  t?: (key: string, options?: Record<string, unknown>) => string,
): string {
  const translate =
    t ||
    ((key: string, options?: Record<string, unknown>) => {
      let value = String(options?.defaultValue ?? key);
      for (const [name, replacement] of Object.entries(options || {})) {
        if (name !== "defaultValue") {
          value = value.replaceAll(`{{${name}}}`, String(replacement));
        }
      }
      return value;
    });
  if (remainingSeconds == null || !Number.isFinite(remainingSeconds)) {
    return "—";
  }
  const totalMinutes = Math.abs(Math.round(remainingSeconds / 60));
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  const duration =
    days > 0
      ? `${days}d${hours > 0 ? ` ${hours}h` : ""}`
      : hours > 0
        ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`
        : `${minutes}m`;
  if (remainingSeconds < 0) {
    return translate("ui.copy.slaOverdueDuration", {
      defaultValue: "Overdue: {{duration}}",
      duration,
    });
  }
  return translate("ui.copy.slaDueDuration", {
    defaultValue: "Due: {{duration}}",
    duration,
  });
}

export function slaClockBadgeVariant(
  state: SlaClockState,
): "danger" | "warning" | "secondary" {
  if (state === "overdue") return "danger";
  if (state === "approaching") return "warning";
  return "secondary";
}

export type ReviewCockpitPanelProps = {
  rows: ReviewCockpitRow[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onOpenStudy: (row: ReviewCockpitRow) => void;
};

export default function ReviewCockpitPanel({
  rows,
  activeIndex,
  onActiveIndexChange,
  onOpenStudy,
}: ReviewCockpitPanelProps) {
  const { t } = useTranslation("features");
  return (
    <section
      className="review-cockpit mb-3"
      data-testid="review-cockpit"
      aria-label={t("ui.copy.eegReviewCockpitAria", {
        defaultValue: "EEG review cockpit",
      })}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          onActiveIndexChange(Math.min(rows.length - 1, activeIndex + 1));
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          onActiveIndexChange(Math.max(0, activeIndex - 1));
        } else if (event.key === "Enter" && rows[activeIndex]) {
          onOpenStudy(rows[activeIndex]);
        }
      }}
      tabIndex={0}
    >
      <h3 className="h6 mb-2"><Trans ns="features" i18nKey="ui.copy.d7ab9db55cd1" /></h3>
      <div className="table-responsive" tabIndex={0}>
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.886cbff9d9df" /></th>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.de5c57ff7257" /></th>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.dc66fb7adeb4" /></th>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.352a763deeed" /></th>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.ee45c30326b7" /></th>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.e55df441e895" /></th>
              <th scope="col"><Trans ns="features" i18nKey="ui.copy.62b12a5b3d7d" /></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.studyId}
                data-testid={`review-cockpit-row-${row.studyId}`}
                className={index === activeIndex ? "table-active" : undefined}
                onClick={() => onActiveIndexChange(index)}
                onDoubleClick={() => onOpenStudy(row)}
              >
                <td>
                  <span
                    className={`badge text-bg-${row.priority === "stat" ? "danger" : row.priority === "urgent" ? "warning" : "secondary"}`}
                  >
                    {row.priority}
                  </span>
                </td>
                <td>{row.patientLabel}</td>
                <td>
                  {row.qeegThumbnailUrl ? (
                    <img
                      src={row.qeegThumbnailUrl}
                      alt={t("ui.copy.qeegThumbnailAlt", {
                        defaultValue: "qEEG thumbnail for {{patient}}",
                        patient: row.patientLabel,
                      })}
                      width={72}
                      height={28}
                    />
                  ) : (
                    <span className="text-muted"><Trans ns="features" i18nKey="ui.copy.a359df22b2b8" /></span>
                  )}
                </td>
                <td>{row.aiCandidateCount}</td>
                <td>{row.reportStatus}</td>
                <td>
                  {row.assignedTo || (
                    <Trans ns="common" i18nKey="unassigned" />
                  )}
                </td>
                <td>
                  {row.slaState === "not_applicable" ? (
                    "—"
                  ) : (
                    <span
                      className={`badge text-bg-${slaClockBadgeVariant(row.slaState)}`}
                    >
                      {formatSlaClock(row.slaRemainingSeconds, t)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
